// ─── Enums ───────────────────────────────────────────────────────────────────

export type DegreeLevel = "licence" | "master" | "doctorat" | "hdr";

export type ThesisType = "physical" | "digital" | "both";

export type ThesisLanguage = "fr" | "en" | "other";

export type ThesisStatus = "pending" | "approved" | "archived" | "rejected";

// ─── Search Params ───────────────────────────────────────────────────────────

export interface ThesisSearchParams {
  /** Full-text search query */
  q?: string;
  /** Filter by department UUID */
  department_id?: string;
  /** Filter by faculty UUID (used to cascade department filter) */
  faculty_id?: string;
  /** Filter by degree level */
  degree_level?: DegreeLevel | DegreeLevel[];
  /** Filter by thesis type */
  thesis_type?: ThesisType;
  /** Filter by thesis language */
  thesis_language?: ThesisLanguage;
  /** Filter: defense_date >= year_from */
  year_from?: number;
  /** Filter: defense_date <= year_to */
  year_to?: number;
  /** Filter by thesis status (admin only) */
  status?: ThesisStatus;
  /** Minimum rating filter (1-5) */
  rating_min?: number;
  /** Sort field */
  sort_by?: string;
  /** Sort order */
  sort_order?: "asc" | "desc";
  /** Page number (1-based) */
  page?: number;
  /** Items per page */
  per_page?: number;
}

// ─── Response Types ──────────────────────────────────────────────────────────

/** Compact thesis item returned in list/search endpoints */
export interface ThesisListItem {
  id: string;
  title_en: string | null;
  title_fr: string | null;
  author_name: string;
  department_name: string | null;
  degree_level: DegreeLevel;
  academic_year: string;
  thesis_type: ThesisType;
  status: ThesisStatus;
  cover_image_url: string | null;
  avg_rating: number;
  views_count: number;
  defense_date: string;
}

/** Category info attached to a thesis */
export interface CategoryInfo {
  id: string;
  name_en: string;
  name_fr: string;
  slug: string;
}

/** Full thesis detail returned by GET /api/theses/:id */
export interface ThesisResponse {
  id: string;
  title_en: string | null;
  title_fr: string | null;
  abstract_en: string | null;
  abstract_fr: string | null;
  author_id: string;
  author_name: string;
  supervisor_name: string;
  co_supervisors: string[];
  department_id: string;
  department_name: string | null;
  faculty_name: string | null;
  degree_level: DegreeLevel;
  defense_date: string;
  academic_year: string;
  keywords: string[];
  thesis_language: ThesisLanguage;
  page_count: number | null;
  thesis_type: ThesisType;
  physical_location: string | null;
  physical_copies: number;
  cover_image_url: string | null;
  status: ThesisStatus;
  isbn: string | null;
  views_count: number;
  avg_rating: number;
  rating_count: number;
  categories: CategoryInfo[];
  citation: string;
  added_by: string;
  created_at: string;
  updated_at: string;
}

// ─── Create / Update DTOs ────────────────────────────────────────────────────

export interface CreateThesisPayload {
  title_en?: string | null;
  title_fr?: string | null;
  abstract_en?: string | null;
  abstract_fr?: string | null;
  author_id: string;
  supervisor_name: string;
  co_supervisors?: string[];
  department_id: string;
  degree_level: DegreeLevel;
  defense_date: string;
  academic_year: string;
  keywords?: string[];
  thesis_language: ThesisLanguage;
  page_count?: number | null;
  thesis_type: ThesisType;
  physical_location?: string | null;
  physical_copies?: number;
  isbn?: string | null;
  category_ids?: string[];
}

export interface UpdateThesisPayload {
  title_en?: string | null;
  title_fr?: string | null;
  abstract_en?: string | null;
  abstract_fr?: string | null;
  author_id?: string;
  supervisor_name?: string;
  co_supervisors?: string[];
  department_id?: string;
  degree_level?: DegreeLevel;
  defense_date?: string;
  academic_year?: string;
  keywords?: string[];
  thesis_language?: ThesisLanguage;
  page_count?: number | null;
  thesis_type?: ThesisType;
  physical_location?: string | null;
  physical_copies?: number;
  isbn?: string | null;
  category_ids?: string[];
}

export interface UpdateThesisStatusPayload {
  status: ThesisStatus;
}

// ─── Reading Session ─────────────────────────────────────────────────────────

export interface ReadingSessionResponse {
  session_token: string;
  page_count: number;
  thesis_title: string;
}

export interface ReadingProgress {
  last_page: number;
  total_pages: number;
  percentage: number;
}

// ─── Citation Formats ────────────────────────────────────────────────────────

export type CitationFormat = "apa" | "mla" | "chicago" | "bibtex";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Get the localised title for a thesis, with fallback */
export function getThesisTitle(
  thesis: { title_en: string | null; title_fr: string | null },
  lang: string,
): { title: string; isFallback: boolean } {
  if (lang === "fr") {
    if (thesis.title_fr) return { title: thesis.title_fr, isFallback: false };
    if (thesis.title_en) return { title: thesis.title_en, isFallback: true };
  } else {
    if (thesis.title_en) return { title: thesis.title_en, isFallback: false };
    if (thesis.title_fr) return { title: thesis.title_fr, isFallback: true };
  }
  return { title: "Untitled", isFallback: true };
}

/** Get the localised abstract for a thesis, with fallback */
export function getThesisAbstract(
  thesis: { abstract_en: string | null; abstract_fr: string | null },
  lang: string,
): { text: string | null; isFallback: boolean } {
  if (lang === "fr") {
    if (thesis.abstract_fr) return { text: thesis.abstract_fr, isFallback: false };
    if (thesis.abstract_en) return { text: thesis.abstract_en, isFallback: true };
  } else {
    if (thesis.abstract_en) return { text: thesis.abstract_en, isFallback: false };
    if (thesis.abstract_fr) return { text: thesis.abstract_fr, isFallback: true };
  }
  return { text: null, isFallback: false };
}

/** Get the localised category name */
export function getCategoryName(
  category: { name_en: string; name_fr: string },
  lang: string,
): string {
  return lang === "fr" ? category.name_fr : category.name_en;
}

/** Degree level color mapping for badges */
export const degreeLevelColors: Record<DegreeLevel, string> = {
  licence: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
  master: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  doctorat: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
  hdr: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
};

/** Thesis type color mapping for badges */
export const thesisTypeColors: Record<ThesisType, string> = {
  physical: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
  digital: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
  both: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
};

/** Thesis status color mapping for badges */
export const thesisStatusColors: Record<ThesisStatus, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
  approved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
  archived: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};
